import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { FolderPropertiesDialogProps, MetadataFieldDef } from '../AssetExplorer.types';
import { downscaleImageForFolderThumbnail } from '../AssetExplorer.api';
import { useAssetExplorerApi } from '../AssetExplorerConfig';
import {
    MetadataSchemaBuilder,
    SchemaBuilderField,
    toSchemaBuilderFields,
} from './MetadataSchemaBuilder';

export const FolderPropertiesDialog: React.FC<FolderPropertiesDialogProps> = ({
    open,
    folderId,
    onClose,
    onSaved,
}) => {
    const { fetchFolderThumbnailAsDataUrl, getFolderProperties, saveFolderProperties } = useAssetExplorerApi();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [name, setName] = useState('');
    const [title, setTitle] = useState('');
    const [isRoot, setIsRoot] = useState(false);
    const [inheritFromParent, setInheritFromParent] = useState(true);
    const [fields, setFields] = useState<SchemaBuilderField[]>([]);
    const [inheritedLabel, setInheritedLabel] = useState('');
    const [hasExistingThumbnail, setHasExistingThumbnail] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [thumbnailBase64, setThumbnailBase64] = useState<string | null>(null);
    const [thumbnailContentType, setThumbnailContentType] = useState<string | null>(null);
    const [removeThumbnail, setRemoveThumbnail] = useState(false);
    const [applyDefaultsToDescendants, setApplyDefaultsToDescendants] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (!open || !folderId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            setThumbnailBase64(null);
            setThumbnailContentType(null);
            setRemoveThumbnail(false);
            setApplyDefaultsToDescendants(false);
            try {
                const data = await getFolderProperties(folderId);
                if (cancelled) return;
                setName(data.name);
                setTitle(data.title || '');
                setIsRoot(data.isRoot);
                setInheritFromParent(data.isRoot ? false : data.inheritFromParent);
                const own = data.inheritFromParent ? data.storedFields : data.fields;
                setFields(toSchemaBuilderFields(own || []));
                const ancestorTitle = data.effectiveSchema.inheritedFromTitle || data.effectiveSchema.inheritedFromName;
                setInheritedLabel(ancestorTitle);
                setHasExistingThumbnail(data.hasThumbnail);
                if (data.hasThumbnail) {
                    const url = await fetchFolderThumbnailAsDataUrl(folderId, data.thumbnailUpdatedAt);
                    if (!cancelled) setPreviewUrl(url);
                } else {
                    setPreviewUrl(null);
                }
            } catch (err: any) {
                if (!cancelled) setError(err.message || 'Failed to load folder properties');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, folderId]);

    const handleClose = () => {
        if (saving) return;
        onClose();
    };

    const handlePickThumbnail = async (file: File | null) => {
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            setError('Thumbnail must be an image');
            return;
        }
        try {
            const result = await downscaleImageForFolderThumbnail(file);
            setThumbnailBase64(result.base64);
            setThumbnailContentType(result.contentType);
            setRemoveThumbnail(false);
            setPreviewUrl(`data:${result.contentType};base64,${result.base64}`);
            setError(null);
        } catch (err: any) {
            setError(err.message || 'Could not process thumbnail');
        }
    };

    const toPayloadFields = (): MetadataFieldDef[] =>
        fields.map((field, index) => ({
            fieldKey: field.fieldKey,
            label: field.label,
            type: field.type,
            required: field.required,
            multiple: field.multiple,
            options: field.options,
            helpText: field.helpText,
            order: index,
            defaultValue: field.defaultValue,
        }));

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const saved = await saveFolderProperties(folderId, {
                title: title.trim(),
                inheritFromParent: isRoot ? false : inheritFromParent,
                fields: isRoot || !inheritFromParent ? toPayloadFields() : undefined,
                thumbnailBase64: thumbnailBase64 || undefined,
                thumbnailContentType: thumbnailContentType || undefined,
                removeThumbnail: removeThumbnail || undefined,
                applyDefaultsToDescendants: applyDefaultsToDescendants || undefined,
            });
            onSaved({ defaultsAppliedCount: saved.defaultsAppliedCount });
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to save folder properties');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsOutlinedIcon color="secondary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Folder Properties
                </Typography>
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                ) : (
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label="Folder name"
                            value={name}
                            fullWidth
                            disabled
                            helperText="Name cannot be changed because it is used in the folder path."
                        />
                        <TextField
                            label="Display title"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            disabled={saving}
                            color="secondary"
                            placeholder={name}
                            helperText="Shown in the explorer. Leave blank to use the folder name."
                        />

                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Thumbnail
                            </Typography>
                            {previewUrl && !removeThumbnail ? (
                                <Box
                                    component="img"
                                    src={previewUrl}
                                    alt="Folder thumbnail"
                                    sx={{
                                        width: 120,
                                        height: 120,
                                        objectFit: 'cover',
                                        borderRadius: 1,
                                        border: 1,
                                        borderColor: 'divider',
                                        mb: 1,
                                        display: 'block',
                                    }}
                                />
                            ) : null}
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    hidden
                                    onChange={(e) => handlePickThumbnail(e.target.files?.[0] || null)}
                                />
                                <Button
                                    variant="outlined"
                                    color="secondary"
                                    disabled={saving}
                                    onClick={() => fileInputRef.current?.click()}
                                    sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                                >
                                    {previewUrl && !removeThumbnail ? 'Replace thumbnail' : 'Upload thumbnail'}
                                </Button>
                                {(previewUrl || hasExistingThumbnail) && !removeThumbnail && (
                                    <Button
                                        color="error"
                                        disabled={saving}
                                        onClick={() => {
                                            setRemoveThumbnail(true);
                                            setThumbnailBase64(null);
                                            setPreviewUrl(null);
                                        }}
                                    >
                                        Remove
                                    </Button>
                                )}
                            </Stack>
                        </Box>

                        {!isRoot && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={inheritFromParent}
                                        onChange={(e) => setInheritFromParent(e.target.checked)}
                                        disabled={saving}
                                    />
                                }
                                label={
                                    inheritFromParent && inheritedLabel
                                        ? `Inherit metadata schema from parent (${inheritedLabel})`
                                        : 'Inherit Metadata Schema from parent'
                                }
                            />
                        )}

                        {(isRoot || !inheritFromParent) && (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Metadata schema
                                </Typography>
                                <MetadataSchemaBuilder fields={fields} onChange={setFields} disabled={saving} />
                            </Box>
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    color="secondary"
                                    checked={applyDefaultsToDescendants}
                                    onChange={(e) => setApplyDefaultsToDescendants(e.target.checked)}
                                    disabled={saving}
                                />
                            }
                            label="Apply Default Metadata to all assets"
                        />
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                <Button
                    onClick={handleClose}
                    disabled={saving}
                    variant="outlined"
                    color="secondary"
                    sx={{ borderWidth: 2, '&:hover': { borderWidth: 2 } }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleSave}
                    disabled={saving || loading}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SettingsOutlinedIcon />}
                >
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FolderPropertiesDialog;
